import {
  createToken,
  Lexer,
  CstParser,
  type CstNode,
  type ILexingResult,
  type IToken,
  tokenLabel,
} from 'chevrotain';
import DiagramError from '../common/DiagramError';

const textStringPattern1 = /"(?:[^\\"]|\\(?:[bfnrtv"\\/]|u[0-9a-fA-F]{4}))*"/y;
const textStringPattern2 = /'(?:[^\\]|\\(?:[bfnrtv'\\/]|u[0-9a-fA-F]{4}))*'/y;
const textLiteralPattern = /[^/#:;|,{}\-<\n[\]]+/y;

function matchText(text: string, startOffset: number) {
  textStringPattern1.lastIndex = startOffset;
  let execResult = textStringPattern1.exec(text);
  if (execResult !== null) {
    const fullMatch = execResult[0];
    // @ts-expect-error - Adding custom property to RegExpExecArray
    execResult.payload = fullMatch.substring(1, fullMatch.length - 1);
    return execResult;
  }

  textStringPattern2.lastIndex = startOffset;
  execResult = textStringPattern2.exec(text);
  if (execResult !== null) {
    const fullMatch = execResult[0];
    // @ts-expect-error - Adding custom property to RegExpExecArray
    execResult.payload = fullMatch.substring(1, fullMatch.length - 1);
    return execResult;
  }

  textLiteralPattern.lastIndex = startOffset;
  execResult = textLiteralPattern.exec(text);
  if (execResult !== null) {
    const fullMatch = execResult[0];
    // Remove trailing whitespace that the regex may have matched.
    execResult[0] = fullMatch.trim();
    // @ts-expect-error - Adding custom property to RegExpExecArray
    execResult.payload = fullMatch.trim();
    return execResult;
  }

  return null;
}

const DiagramType = createToken({
  name: 'DiagramType',
  pattern: /flowchart|cycle|hierarchy/,
});
const Text = createToken({
  name: 'Text',
  pattern: matchText,
  line_breaks: false,
});
const LCurly = createToken({ name: 'LCurly', pattern: /{/ });
const RCurly = createToken({ name: 'RCurly', pattern: /}/ });
const LSquare = createToken({ name: 'LSquare', pattern: /\[/ });
const RSquare = createToken({ name: 'RSquare', pattern: /]/ });
const Comma = createToken({ name: 'Comma', pattern: /,/ });
const Colon = createToken({ name: 'Colon', pattern: /:/ });
const ArrowLeft = createToken({ name: 'ArrowLeft', pattern: /<-/ });
const ArrowRight = createToken({ name: 'ArrowRight', pattern: /->/ });
const ArrowLeftLong = createToken({ name: 'ArrowLeftLong', pattern: /<--/ });
const ArrowRightLong = createToken({ name: 'ArrowRightLong', pattern: /-->/ });
const DoubleArrow = createToken({ name: 'DoubleArrow', pattern: /<->/ });
const Dash = createToken({ name: 'Dash', pattern: /--/ });
const DashLong = createToken({ name: 'DashLong', pattern: /---/ });

const Newline = createToken({
  name: 'Newline',
  pattern: /\n/,
  line_breaks: true,
});
const WhiteSpace = createToken({
  name: 'WhiteSpace',
  pattern: /[ \t]+/,
});
const CppComment = createToken({
  name: 'CppComment',
  pattern: /\/\/.*/,
});
const RubyComment = createToken({
  name: 'RubyComment',
  pattern: /#.*/,
});
const Comment = createToken({
  name: 'Comment',
  pattern: /\/\*[^*]*\*\//,
  line_breaks: true,
});

const whitespaceTokens = [WhiteSpace, CppComment, RubyComment, Comment];

const allTokens = [
  Newline,
  WhiteSpace,
  CppComment,
  RubyComment,
  Comment,
  LCurly,
  RCurly,
  LSquare,
  RSquare,
  Comma,
  Colon,
  DoubleArrow,
  ArrowLeftLong,
  ArrowRightLong,
  DashLong,
  Dash,
  ArrowLeft,
  ArrowRight,
  DiagramType,
  Text,
];

// Lexer for common diagram syntax.
class CommonLexer extends Lexer {
  constructor() {
    super(allTokens);
  }

  tokenize(text: string, initialMode?: string | undefined): ILexingResult {
    // Add a trailing newline if necesary.
    if (text.length > 0 && text[text.length - 1] !== '\n') {
      text += '\n';
    }
    const result = super.tokenize(text, initialMode);

    // Extract the whitespace tokens and attach them to the adjacent token.
    // TODO: Doesn't handle whitespace at the end of the input.
    result.tokens = result.tokens
      .map((token, index) => {
        if (whitespaceTokens.includes(token.tokenType)) {
          const nextToken = result.tokens[index + 1];
          if (nextToken) {
            // @ts-expect-error - Adding custom property to IToken
            nextToken.precedingWhitespace = token;
          } else {
            // TODO: Trailing whitespace = token.
          }
          return null;
        } else {
          return token;
        }
      })
      .filter((n): n is IToken => !!n);

    return result;
  }
}

type DiagramParserResultType = 'success' | 'error';

interface IDiagramParserOutput {
  result: DiagramParserResultType;
  root: CstNode;
  errors: DiagramError[];
}

// Custom error messages.
const ErrorProvider = {
  buildMismatchTokenMessage: ({
    expected,
    actual,
    previous: _previous,
    ruleName: _ruleName,
  }: {
    expected: any; // Using any here as tokenLabel expects a specific type
    actual: IToken;
    previous?: IToken;
    ruleName?: string;
  }) => {
    return `Expecting ${tokenLabel(expected)} but found '${actual.image}'`;
  },
  buildNotAllInputParsedMessage: ({
    firstRedundant,
    ruleName: _ruleName,
  }: {
    firstRedundant: IToken;
    ruleName?: string;
  }) => {
    return `Didn't expect '${firstRedundant.image}' here`;
  },
  buildEarlyExitMessage: () => {
    return `Expected to find a list here`;
  },
  buildNoViableAltMessage: () => {
    return `Expected to find an alternative here`;
  },
};

// Parser for common diagram syntax.
class CommonParser extends CstParser {
  lexer = new CommonLexer();

  constructor() {
    super(allTokens, {
      nodeLocationTracking: 'full',
      errorMessageProvider: ErrorProvider,
      recoveryEnabled: true,
    });
    this.performSelfAnalysis();
  }

  parse(text: string): IDiagramParserOutput {
    const errors: DiagramError[] = [];
    let result: DiagramParserResultType = 'error';

    const lexResult = this.lexer.tokenize(text);
    this.input = lexResult.tokens;

    // Add lexer errors to the output.
    for (const lexError of lexResult.errors) {
      errors.push(
        new DiagramError(lexError.message, {
          startOffset: lexError.offset,
          endOffset: lexError.offset + lexError.length,
          startLine: lexError.line || 0,
          endLine: lexError.line || 0,
          startColumn: lexError.column || 1,
          endColumn: lexError.column || 1,
        })
      );
    }

    const rootNode: CstNode = this.diagram();

    // Add parser errors to the output.
    for (const parseError of this.errors) {
      const token = parseError.token;
      errors.push(
        new DiagramError(parseError.message, {
          startOffset: token.startOffset,
          endOffset: token.endOffset
            ? token.endOffset
            : token.startOffset + token.image.length + 1,
          startLine: token.startLine || 0,
          endLine: token.endLine || 0,
          startColumn: token.startColumn || 1,
          endColumn: token.endColumn || token.image.length + 1,
        })
      );
    }

    if (rootNode) {
      result = 'success';
    }

    return {
      result,
      root: rootNode,
      errors,
    };
  }

  // Notes for writing rules here:
  // - Always use `LABEL` if there are multiple tokens with the same name. This
  //   allows use to simplify error handling by only considering the [0] token.

  public diagram = this.RULE('diagram', () => {
    this.MANY(() => {
      this.SUBRULE(this.statement);
    });
  });

  private readonly statement = this.RULE('statement', () => {
    this.OPTION(() =>
      this.OR([
        { ALT: () => this.SUBRULE2(this.step) },
        { ALT: () => this.SUBRULE3(this.diagramType) },
      ])
    );
    this.CONSUME(Newline);
  });

  private readonly diagramType = this.RULE('diagramType', () => {
    this.CONSUME(DiagramType);
    this.OPTION(() => this.SUBRULE(this.properties));
  });

  private readonly step = this.RULE('step', () => {
    this.SUBRULE1(this.node, { LABEL: 'FromNode' });
    this.OPTION(() => {
      this.SUBRULE2(this.connector);
      this.SUBRULE3(this.node, { LABEL: 'ToNode' });
    });
  });

  private readonly subDiagram = this.RULE('subDiagram', () => {
    this.CONSUME(LSquare);
    this.OPTION2(() => this.CONSUME2(Newline, { LABEL: 'Newline1' }));
    this.SUBRULE(this.diagram);
    this.OPTION3(() => this.CONSUME3(Newline, { LABEL: 'Newline2' }));
    this.CONSUME(RSquare);
  });

  private readonly node = this.RULE('node', () => {
    this.OPTION1(() => this.SUBRULE1(this.label));
    this.OR([
      { ALT: () => this.CONSUME(Text) },
      { ALT: () => this.SUBRULE4(this.subDiagram) },
    ]);
    this.OPTION(() => this.SUBRULE(this.properties));
  });

  private readonly connector = this.RULE('connector', () => {
    this.OR1([
      { ALT: () => this.CONSUME1(ArrowLeft) },
      { ALT: () => this.CONSUME1(ArrowRight) },
      { ALT: () => this.CONSUME1(DashLong) },
      { ALT: () => this.CONSUME1(DoubleArrow) },
      {
        ALT: () => {
          this.CONSUME1(Dash, { LABEL: 'DashLeft' });
          this.OPTION1(() => this.CONSUME2(Text));
          this.OPTION2(() => this.SUBRULE1(this.properties));
          this.OR2([
            { ALT: () => this.CONSUME3(ArrowRightLong) },
            { ALT: () => this.CONSUME4(Dash, { LABEL: 'DashRight' }) },
          ]);
        },
      },
      {
        ALT: () => {
          this.CONSUME1(ArrowLeftLong);
          this.OPTION3(() => {
            this.OPTION4(() => this.CONSUME5(Text));
            this.OPTION5(() => this.SUBRULE2(this.properties));
            this.OR3([
              { ALT: () => this.CONSUME6(ArrowRightLong) },
              { ALT: () => this.CONSUME7(Dash, { LABEL: 'DashRight' }) },
            ]);
          });
        },
      },
    ]);
  });

  private readonly label = this.RULE('label', () => {
    this.CONSUME(Text);
    this.CONSUME(Colon);
  });

  private readonly properties = this.RULE('properties', () => {
    this.CONSUME(LCurly);
    this.OPTION1(() => this.CONSUME2(Newline));
    this.MANY_SEP({
      SEP: Comma,
      DEF: () => this.SUBRULE(this.property),
    });
    this.OPTION2(() => this.CONSUME3(Newline));
    this.CONSUME(RCurly);
  });

  private readonly property = this.RULE('property', () => {
    this.OPTION1(() => this.CONSUME1(Newline, { LABEL: 'Newline1' }));
    this.CONSUME2(Text, { LABEL: 'NameText' });
    this.OPTION2(() => this.CONSUME3(Newline, { LABEL: 'Newline2' }));
    this.CONSUME4(Colon);
    this.OPTION3(() => this.CONSUME5(Newline, { LABEL: 'Newline3' }));
    this.CONSUME6(Text, { LABEL: 'ValueText' });
    this.OPTION4(() => this.CONSUME7(Newline, { LABEL: 'Newline4' }));
  });
}

const commonParser = new CommonParser();
const CommonVisitor = commonParser.getBaseCstVisitorConstructor();

export default commonParser;
export { CommonParser, CommonVisitor };
export type { IDiagramParserOutput };
